// The checks AHFC runs itself, as the enrolled Medicaid provider, and how a
// person's record of them reads. Runtime-neutral: the billing self-audit reads
// the same exclusion window as the staff profile.
//
// A check is two things: the date and name on `staff` (who looked, and when)
// and the proof, a staff_documents row per file. The proof rows carry the
// check's own timestamp in sent_at, which is how a file is tied to the check it
// proves, and each re-check keeps its own rows, so the history stays.
//
// Never delegated to a sending agency, so never in staff_certifications, where
// verifying_party could say otherwise. These doc_types are deliberately absent
// from the agency portal's allow-list (_shared/agency-documents.ts).

// OIG updates the LEIE monthly. Georgia Medicaid requires exclusion screening
// before hire and monthly after; 35 days allows for a month's working calendar.
export const EXCLUSION_STALE_DAYS = 35;

const DAY_MS = 86_400_000;

export const SCREENING_KINDS = {
  registry: {
    atField: 'registry_checked_at', byField: 'registry_checked_by',
    label: 'Registry check',
    detail: 'Nurse Aide Registry and Georgia Sex Offender Registry searched',
    eventType: 'registry_checked',
    docType: 'registry_check',
    sources: [{ label: 'GBI registry', href: 'https://state.sor.gbi.ga.gov/sort_public/' }],
  },
  exclusion: {
    atField: 'exclusion_checked_at', byField: 'exclusion_checked_by',
    label: 'Exclusion screening',
    detail: 'GA OIG Exclusions List, federal LEIE and SAM searched',
    eventType: 'exclusion_checked',
    docType: 'exclusion_check',
    staleDays: EXCLUSION_STALE_DAYS,
    sources: [{ label: 'LEIE', href: 'https://exclusions.oig.hhs.gov/' }],
  },
  license: {
    atField: 'license_verified_at', byField: 'license_verified_by',
    label: 'License verification',
    detail: 'RN/LPN license verified at primary source (GCHEXS-exempt)',
    eventType: 'license_verified',
    docType: 'license_verification',
    sources: [{ label: 'Nursys', href: 'https://www.nursys.com/LQC/LQCTerms.aspx' }],
  },
};

const isNurse = (s) => ['RN', 'LPN'].includes(s?.position_code);

/** Which checks this person's file needs, in the order they are shown. */
export const screeningKindsFor = (s) => (isNurse(s) ? ['license', 'registry', 'exclusion'] : ['registry', 'exclusion']);

/**
 * When a check happened. Today means now; an earlier day means midday, so it
 * reads as that day in any timezone. A check cannot be recorded for a day that
 * has not happened.
 */
export function checkTimestamp(checkedOn, now = new Date()) {
  if (!checkedOn) return now.toISOString();
  const [y, m, d] = String(checkedOn).split('-').map(Number);
  if (!y || !m || !d) throw new Error('That is not a date.');
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = new Date(y, m - 1, d);
  if (day > startOfDay) throw new Error('A check cannot be dated in the future.');
  if (day.getTime() === startOfDay.getTime()) return now.toISOString();
  return new Date(y, m - 1, d, 12).toISOString();
}

/** What recording a check writes to `staff`. */
export function screeningPatch(kind, at, by) {
  const spec = SCREENING_KINDS[kind];
  if (!spec) throw new Error(`Unknown screening kind: ${kind}`);
  const patch = { [spec.atField]: at, [spec.byField]: by || null };
  // The license is the nurse's background check: the GCHEXS exemption holds
  // only while it is current, so verifying it is what clears them.
  if (kind === 'license') Object.assign(patch, { bg_check_type: 'license_verification', bg_check_status: 'cleared', bg_check_cleared_at: at });
  return patch;
}

/** The staff_documents rows that hold a check's proof, one per file. */
export function proofRows(staffId, kind, at, proofs = []) {
  const spec = SCREENING_KINDS[kind];
  const day = at.slice(0, 10);
  return proofs.map((p) => ({
    staff_id: staffId, doc_type: spec.docType, doc_label: `${spec.label} proof (${day}): ${p.name}`,
    status: 'on_file', pdf_url: p.url, sent_at: at,
  }));
}

/**
 * How one check stands for one person.
 * state: 'missing' (never recorded, or past its window — it needs doing),
 *        'pending' (recorded, but nothing attached to show for it),
 *        'complete'.
 */
export function screeningState(staff, kind, documents = [], now = new Date()) {
  const spec = SCREENING_KINDS[kind];
  const at = staff?.[spec.atField] || null;
  const by = staff?.[spec.byField] || null;
  if (!at) return { kind, spec, state: 'missing', at: null, by: null, days: null, stale: false, proofs: [] };
  const when = new Date(at).getTime();
  const days = Math.floor((now.getTime() - when) / DAY_MS);
  const stale = !!spec.staleDays && days > spec.staleDays;
  const proofs = documents.filter((d) => d.doc_type === spec.docType && d.pdf_url && d.sent_at && new Date(d.sent_at).getTime() === when);
  return { kind, spec, state: stale ? 'missing' : proofs.length ? 'complete' : 'pending', at, by, days, stale, proofs };
}
